import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  OnInit,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { normalize } from '@/utils/normalizeString';
import { take } from 'rxjs/operators';
import { interval } from 'rxjs';

export interface Option {
  id: string;
  value: string;
}

@Component({
  selector: 'app-select-list[isLoading]',
  templateUrl: './select-list.component.html',
  styleUrls: ['./select-list.component.css'],
})
export class SelectListComponent implements OnInit {
  @Input() title: string = '';
  @Input() placeHolder: string = '';
  @Input() hintText: string = '';
  @Input() tooltip: string = '';
  @Input() textInformation: string = '';
  @Input() behavior: 'ON_FOCUS' | 'ON_TEXT_CHANGE' = 'ON_TEXT_CHANGE';
  @Input() shouldFilter: boolean = false;
  @Input() shouldShowArrow: boolean = false;
  @Input() controlForm: FormControl = new FormControl('', [
    Validators.required,
    Validators.min(1),
  ]);
  @Input() optional: boolean = false;
  @Input() biggerOptionSpace: boolean = false;
  @Input() biggerOptionWidth: boolean = false;
  @Input() color: 'default' | 'white' = 'default';
  @Input() isLoading: boolean = false;

  @Input()
  get optionList(): Option[] {
    return this._optionList;
  }
  set optionList(options: Option[]) {
    this._optionList = options;
    this.filteredOptionList = options;
    if (this.behavior === 'ON_FOCUS') {
      this.textInputValue.reset();
      this.selectedValue.reset();
    }
  }
  private _optionList: Option[] = [];

  filteredOptionList: Option[] = [];

  @Output() textInputValueEmitter = new EventEmitter<string>();
  @Output() selectedValueEmitter = new EventEmitter<string>();

  textInputValue = new FormControl('');
  selectedValue = new FormControl('');

  focus: boolean = false;
  containerFocus: boolean = false;

  constructor(private cdr: ChangeDetectorRef) { }

  ngOnInit() {
    this.controlForm.valueChanges.subscribe({
      next: (value) => {
        if (value === null) {
          this.textInputValue.reset();
          this.selectedValue.reset();
        }
      },
    });
  }

  onTextInputChange(dirtyText: string) {
    const text = (dirtyText || '').trim();
    if (text !== this.selectedValue.value) {
      this.textInputValueEmitter.emit(text);
      if (!(text.split('.').length - 1 === text.length)) {
        this.selectedValueEmitter.emit('');
        this.controlForm.patchValue(0);
        if (this.shouldFilter) {
          this.filteredOptionList = this._optionList.filter(
            (option) => normalize(option.value).includes(normalize(text)),
          );
        }
        else {
          this.filteredOptionList = this._optionList;
        }
      }
    }
    else if (!text && this.behavior === 'ON_FOCUS') {
      this.filteredOptionList = this._optionList;
    }
    this.focus = true;
  }

  onSelectValue(event: MouseEvent) {
    const selectedId = (<any>event.target).id as string;
    let selectedValue = this._optionList.find(({ id }) => id === selectedId);
    if (selectedValue) {
      selectedValue = <Option>selectedValue;
      this.selectedValue.setValue(selectedValue.value);
      this.textInputValue.setValue(selectedValue.value);
      this.selectedValueEmitter.emit(selectedValue.id);
      this.filteredOptionList = [];
      this.controlForm.patchValue(+selectedId);
    }
  }

  handleBlur() {
    this.focus = false;
    this.cdr.detectChanges();
  }

  handleBlurr() {
    interval(500)
      .pipe(take(2))
      .subscribe({
        next: (value) => {
          if (value > 0) {
            this.handleBlur();
          }
        },
      });
  }

  handleFocus() {
    this.focus = true;
  }

  alphaNumberOnly(e: any) {  // Accept only alpha numerics, not special characters
    const regex = new RegExp('^[a-zA-Z0-9 -]+$');
    const regexArroba = new RegExp('@');
    const regexEnie = new RegExp('ñ', 'ig');
    const regexUnderscore = new RegExp('_', 'ig');
    const regexDot = new RegExp('.', 'ig');
    const str = String.fromCharCode(!e.charCode ? e.which : e.charCode);
    if ((regex.test(str) || regexArroba.test(str)  || regexEnie.test(str) || regexDot.test(str) || regexUnderscore.test(str))) {
      return true;
    }
    e.preventDefault();
    return false;
  }

  onPaste(event: ClipboardEvent) {
    const clipboardData = event.clipboardData || (window as any).clipboardData;
    let text = clipboardData.getData('text');
    text = [...(<unknown>text as any).matchAll(/[A-Z0-9\@\-\_\ñ\.\s]/ig)].map((x) => x[0]).join('');

    this.textInputValue.setValue(text);

    event.preventDefault();

    return false;
  }
}
